import { z } from 'zod';
import { boardSchema, boardTitleSchema } from './board';
import { cardTitleSchema } from './card';
import { cardDetailSchema } from './card-detail';
import { reconciliationSchema } from './cross-board-move';
import { ulidSchema } from './id';
import { listSchema, listTitleSchema } from './list';

/**
 * Copying cards, lists and boards — the contract layer for block **B35**.
 *
 * `BE-35.1` (`FR-TMPL-013`, copy a card with per-element choices),
 * `BE-35.2` (`FR-TMPL-014`, copy a list — every card it holds), `BE-35.3`
 * (`FR-TMPL-015`, copy a board — titles and descriptions only) and `BE-35.4`
 * (`FR-TMPL-016`, give the duplicate a predictable name). Three routes —
 * `POST /cards/:id/copy`, `POST /lists/:id/copy`, `POST /boards/:id/copy` —
 * each answering with the thing that was created and, where a destination
 * already had its own vocabulary to reconcile against, a report of what the
 * copy cost.
 *
 * ---------------------------------------------------------------------------
 * ⚠ TWO CONTRADICTIONS THE BLOCK BRIEF SETTLED, RECORDED HERE BECAUSE THIS
 * FILE IS WHERE THE SETTLEMENT BECOMES A SHAPE
 * ---------------------------------------------------------------------------
 * **D1 — the carry-over toggle set is FIVE, not four and not six.**
 * `FR-BOARD-027`, `FR-CARD-030` and `FR-TMPL-013` each name a different set;
 * the settled union is `checklists, labels, members, attachments, dates` —
 * the four uncontested elements plus dates (`FR-CARD-030`, corroborated by
 * the block's own `Depends on B26, B27, B24` line). Custom fields and
 * stickers are excluded: neither has a route (`B56`, `B58` are unbuilt), and
 * custom fields carry a second, independent reason — `FR-TMPL-013`'s own
 * gate ("on plans that provide them") names a plan/entitlement model
 * `CLAUDE.md` records as absent from this corpus entirely.
 *
 * **D2 — comments and activity are NEVER copied, on any surface.**
 * `FR-TMPL-014` (list copy) says comments travel; `FR-TMPL-013` (card) and
 * `FR-TMPL-015` (board) say they do not, and `FR-TMPL-014`'s own edge case
 * flags the inconsistency and invites a clone to resolve it. Two documents
 * against one, and the comment/activity pair is non-copyable everywhere in
 * this product — `apps/server/README.md` §37 carries the argument in full,
 * and `apps/web/e2e/FR-TMPL-014.spec.ts` asserts AC2 **inverted** against it.
 * Nothing below carries a `comments` or `activity` field on any of the three
 * bodies or any of the three results, and none ever will: there is no toggle
 * to clear, because there is nothing to copy in the first place.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE THREE TIERS ARE NOT THE SAME SET — READ THIS BEFORE WRITING ANY OF
 * THE THREE BODIES BELOW, LET ALONE ALL THREE IN PARALLEL
 * ---------------------------------------------------------------------------
 * **Card copy** — five independent toggles, each defaulting to `true` (the
 * dialog pre-checks them; `FR-CARD-030`'s "clearing" language presumes they
 * start on). **List copy** — all five, unconditionally, no toggles at all:
 * no requirement offers a per-card choice on a list copy, and copying a list
 * is "copy each of its cards" with everything kept. **Board copy** — NONE of
 * the five, ever: `FR-TMPL-015` limits card content to "titles and
 * descriptions", so a board copy's card loop is title + description and
 * nothing else, on every card, with no toggle to turn checklists or labels
 * back on. What a board copy DOES carry that neither other tier does is the
 * board's whole **label set** (fresh rows, fresh ranks — not the six
 * `createBoard` defaults) and its background; what it never carries, on any
 * tier, is a **member** other than the actor, who becomes the copy's admin
 * alone (`FR-TMPL-015` AC5).
 *
 * `keepCards` on the board body is a fourth, orthogonal kind of switch from
 * the five element toggles above: it decides whether the board's CARDS
 * travel at all (title + description, per card), not which of a card's
 * ELEMENTS travel. A board copy has no version of the other five, because it
 * has already decided none of them travel.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE DESTINATION IS A LIST ID FOR A CARD, A BOARD ID FOR A LIST — NEVER A
 * SIBLING `boardId` ON THE CARD BODY
 * ---------------------------------------------------------------------------
 * `ck_cards_parent` requires `cards.board_id` and `cards.list_id` to agree,
 * and `cross-board-move.ts` already settled this for a move: the destination
 * board is DERIVED from the destination list, and a body naming both
 * independently could disagree with itself with no right answer to the
 * disagreement. `cardCopyBodySchema` below takes exactly `cardMoveToBoardBody
 * Schema`'s stance and, deliberately, not its declaration — `cross-board-
 * move.ts`'s own header states the reason a shared `resolveGap`-style const
 * is not imported across two blocks' bodies, and it applies again here
 * unchanged.
 *
 * A list, in contrast, genuinely has no path segment to disagree with on
 * `POST /lists/:id/copy` — the SOURCE list is the path, and the DESTINATION
 * board is a body field exactly as it is on `createListBodySchema`
 * (`./list`). So `listCopyBodySchema` carries an optional `boardId`; `card
 * CopyBodySchema` carries none, ever.
 *
 * ---------------------------------------------------------------------------
 * ⚠ RECONCILIATION IS REUSED, NOT REINVENTED — AND ONE TIER NEEDS NONE AT ALL
 * ---------------------------------------------------------------------------
 * `FR-TMPL-013`'s own edge case and `FR-TMPL-019`/`FR-TMPL-020`'s wording
 * ("moves **or is copied**") make a cross-board card or list copy subject to
 * the identical label/member reconciliation a cross-board MOVE already is.
 * `reconciliationSchema` is imported from `./cross-board-move` **whole and
 * unmodified** — not extended, not restated with a second declaration for
 * "the copy version" — because it is the same contract answering the same
 * question ("what did crossing a board cost this card's vocabulary"), asked
 * by a second gesture rather than a new one. `cross-board-move.ts`'s own
 * header states the general form for when two routes' shapes merely
 * coincide today ("two routes with the same shape is not two routes with the
 * same contract") — this is the opposite case, where the underlying planners
 * (`matchesDestinationLabel`, `planLabelReconciliation`,
 * `planMemberReconciliation` in `apps/server/src/cards/reconcile.ts`) are
 * themselves shared between move and copy, so the report they produce is
 * shared too, by construction rather than by coincidence.
 *
 * A **same-board** copy needs no reconciliation at all — labels and members
 * apply directly, because there is no second board's vocabulary to match
 * against — and the response still carries the field: the ordinary answer is
 * three empty label arrays and no removed member, `cross-board-move.ts`'s
 * own "empty is the ordinary state" precedent, not a reason to make the
 * field optional.
 *
 * **Board copy carries no `reconciliation` at all**, on neither tier's
 * footing: a board copy does not match the source's labels against a
 * pre-existing destination vocabulary, it COPIES the source's label set
 * wholesale into a board that has none yet, and it never places a card
 * member to reconcile in the first place (`FR-TMPL-015` AC5). There is
 * nothing for the shape to report, so it reports nothing, exactly as
 * `cardMoveToBoardResultSchema`/`listMoveToBoardResultSchema` carry no
 * `custom_field_values` key for a subsystem that does not apply to them
 * either.
 *
 * ---------------------------------------------------------------------------
 * ⚠ NAMING: `keep*`, NOT `include*` — THE CORPUS'S OWN WORD
 * ---------------------------------------------------------------------------
 * `FR-TMPL-013` calls them "independent 'keep' choices"; `FR-TMPL-002`'s and
 * `FR-TMPL-015`'s own acceptance criteria say "given I clear the 'keep
 * cards' choice". `keepCards` is the block brief's own field name for the
 * board body, so the five card-copy toggles are named on the same footing —
 * `keepChecklists`, `keepLabels`, `keepMembers`, `keepAttachments`,
 * `keepDates` — rather than splitting one file between two vocabularies for
 * one idea.
 *
 * ---------------------------------------------------------------------------
 * ⚠ IDENTITY, RANKS, EVENTS AND THE NAMING RULE ITSELF LIVE ELSEWHERE
 * ---------------------------------------------------------------------------
 * Nothing below validates a `shortLink`, a `cardNumber` or a `position` on
 * any body, for `cross-board-move.ts`'s reason restated a third time (SC-2,
 * `OQ-2`): those are server facts, computed inside the transaction from the
 * gap the neighbours name and from the destination's own sequence, never
 * caller-supplied. The suffixing algorithm `FR-TMPL-016` requires
 * (`nextCopyName`) is pure logic with no wire shape of its own — it lives at
 * `apps/server/src/copy/duplicate-name.ts` — and the `title` field below
 * merely carries what the CALLER typed (or omitted); reading `.trim()` on
 * the imported title primitives as a form of suffixing would be a category
 * error, because a suffix is appended server-side to a title this schema
 * has already accepted, never computed here. The `card.copied_from_card` /
 * `list.created` / `board.created` payload contracts are `./events`'
 * concern and this file declares no event name and no payload shape — see
 * that module for the registration this block owes it.
 *
 * ---------------------------------------------------------------------------
 * THIS IS NOT `BE-02.1`
 * ---------------------------------------------------------------------------
 * `BE-02.1` — the full Phase 1 object model — is a different item and stays
 * PENDING. "Copying" is not one of its eight nouns; every view below either
 * *is* an imported shape (`cardDetailSchema`, `listSchema`, `boardSchema`,
 * `reconciliationSchema`) or a thin envelope around one, on
 * `cross-board-move.ts`'s and `board-archive.ts`'s footing. Seven of the
 * eight have a file; membership is still the one that does not, and
 * `ls packages/shared/src` is still the instruction.
 */

// ---------------------------------------------------------------------------
// Inputs — what the three routes ACCEPT.
//
// `z.strictObject` on all three, per every body in this package: an unknown
// key is a 422 naming the field rather than a silently stripped one, which
// matters more here than on most bodies because every plausible mistake a
// caller could make below (a `boardId` on the card body, a `position` on the
// list body, a per-element toggle on the board body) would otherwise get a
// correct-looking 200 to a different question than the one it asked.
// ---------------------------------------------------------------------------

/**
 * `POST /cards/:id/copy` — `FR-TMPL-013`.
 *
 * The card is in the path; the destination **list** is in the body, exactly
 * as `cardMoveToBoardBodySchema` (`./cross-board-move`) already settled for
 * a move — and this body is deliberately shape-compatible with it on the
 * three positioning fields, because the destination is derived from the
 * list on both routes for the identical reason (`ck_cards_parent`).
 *
 * ⚠ **No `boardId`, ever — see the module header.** The destination board is
 * whichever board `listId` names; a copy that lands on the source card's own
 * board is a same-board copy and needs no reconciliation, a copy that lands
 * elsewhere is a cross-board copy and the response's `reconciliation` is
 * where that cost is reported.
 */
export const cardCopyBodySchema = z.strictObject({
  listId: ulidSchema.describe(
    'The list the copy lands in — on ANY board the actor may write, not ' +
      'only the source card’s own. Required; the destination board is ' +
      'DERIVED from this list and is deliberately not a field of its own ' +
      '(ck_cards_parent, and cardMoveToBoardBodySchema’s identical stance). ' +
      'A list on the source card’s own board is a same-board copy; any ' +
      'other list triggers the label/member reconciliation FR-TMPL-019 and ' +
      'FR-TMPL-020 already settled for a cross-board move (FR-TMPL-013’s ' +
      'own edge case: "copying to a different board triggers the ' +
      'reconciliation rules").',
  ),
  previousCardId: ulidSchema
    .nullable()
    .default(null)
    .describe(
      'The card immediately ABOVE the gap the copy lands in, in the ' +
        'destination list. Send this alone to land the copy at the bottom ' +
        'of that list — the ordinary case. Same spelling and null ' +
        'convention as cardMoveToBoardBodySchema’s field of this name, ' +
        'though the two schemas remain separate declarations (that file’s ' +
        'own header states why a shared const is not exported for it).',
    ),
  nextCardId: ulidSchema
    .nullable()
    .default(null)
    .describe(
      'The card immediately BELOW the gap the copy lands in. Send this ' +
        'alone to land it at the top. Omitting BOTH neighbours is legal ' +
        'and means the bottom of the destination list — how a copy lands ' +
        'in an empty list.',
    ),
  title: cardTitleSchema
    .optional()
    .describe(
      'The copy’s title. Omit to inherit the source’s title EXACTLY — ' +
        'FR-TMPL-016 names only lists and boards for the disambiguating ' +
        'suffix on an unchanged name; a card copy is never suffixed, ' +
        'because card titles are not unique anywhere in the product and ' +
        'no requirement asks them to be here. Provide a value to use it ' +
        'exactly, which is the same outcome the omitted case already ' +
        'produces — FR-TMPL-016 states "no suffix ever" as a general rule ' +
        'this schema must not special-case away for a title that happens ' +
        'to equal the source’s. (`.trim()` here is cardTitleSchema’s own ' +
        'normalisation of what was typed, not a form of suffixing — no ' +
        'suffix logic touches a card copy at all; see the module header.)',
    ),
  keepChecklists: z
    .boolean()
    .default(true)
    .describe(
      'Carry the card’s checklists and their items across. Items are ' +
        'ALWAYS copied unchecked regardless of this toggle’s value — ' +
        'FR-CARD-030 AC4 — and each item’s assignee and per-item due date ' +
        'are dropped, matching FR-CARD-019’s shipped "copy items from" ' +
        'precedent (apps/server/src/routes/checklists.ts). Defaults to ' +
        'true: the dialog pre-checks every "keep" choice.',
    ),
  keepLabels: z
    .boolean()
    .default(true)
    .describe(
      'Carry the card’s labels across. On a same-board copy the SAME ' +
        'labels apply directly; on a cross-board copy each is matched ' +
        'against the destination’s set by the (name, colour) pair ' +
        '(FR-TMPL-019) and the outcome rides in the response’s ' +
        'reconciliation. Defaults to true.',
    ),
  keepMembers: z
    .boolean()
    .default(true)
    .describe(
      'Carry the card’s members across. On a cross-board copy a member ' +
        'who holds no board_members row on the destination board is ' +
        'dropped and the drop is reported (FR-TMPL-020), exactly as it is ' +
        'for a move. Defaults to true — clearing it is FR-TMPL-013 AC1’s ' +
        '"copy a card with the members choice cleared" and produces a ' +
        'copy with no members while the original keeps its own.',
    ),
  keepAttachments: z
    .boolean()
    .default(true)
    .describe(
      'Carry the card’s attachments across. A link attachment is a row ' +
        'copy; a file attachment is copied byte-for-byte to a fresh, ' +
        'card-scoped storage key — never the source’s key, which would ' +
        'let deleting the source’s attachment destroy the copy’s bytes. ' +
        'Defaults to true.',
    ),
  keepDates: z
    .boolean()
    .default(true)
    .describe(
      'Carry the card’s start date, due date and due-date reminder ' +
        'offset across. Defaults to true.',
    ),
});

export type CardCopyBody = z.infer<typeof cardCopyBodySchema>;

/**
 * `POST /lists/:id/copy` — `FR-TMPL-014`.
 *
 * The list is in the path; the destination **board** is an OPTIONAL body
 * field, on `createListBodySchema`'s footing rather than
 * `cardCopyBodySchema`'s above — a list genuinely has no path segment for
 * `boardId` to disagree with, unlike a card copy where the destination board
 * is derived from a required `listId`.
 *
 * No per-element toggles: `FR-TMPL-014` offers none, and a list copy is
 * "copy each of the source list's cards" with every one of the five
 * elements kept, on every card — see the module header's tier table.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THE NULL PAIR HAS A THIRD MEANING HERE, AND IT DEPENDS ON `boardId` —
 * READ BOTH BRANCHES BEFORE TOUCHING EITHER NEIGHBOUR FIELD
 * ---------------------------------------------------------------------------
 * `./list`'s own two bodies already give the null pair two DIFFERENT
 * meanings on the same two field names — `createListBodySchema`'s pair means
 * "the far right of the strip" (the common case), `listPositionPatchSchema`
 * refuses the pair outright with a 422 ("move this list to nowhere" is not a
 * request anyone can have meant). **This is neither of those two.** Here
 * both null means:
 *
 *   - **`boardId` omitted (the ordinary case, copying onto the source
 *     list's own board):** "immediately after the source list" —
 *     `FR-TMPL-014`'s own behaviour text, verbatim: "The new list is placed
 *     immediately after the source unless a position is chosen."
 *   - **`boardId` provided, naming another board:** "immediately after the
 *     source" is meaningless on a board the source list is not even on, so
 *     both null means **append to the destination board's strip** — the
 *     same reading `rankForListAppendedToBoard` in
 *     `apps/server/src/routes/lists.ts` already gives a cross-board list
 *     MOVE, built for exactly this.
 *
 * An explicit `previousListId`/`nextListId` names a gap on the DESTINATION
 * board in both branches, exactly as the create body's explicit neighbours
 * do; a neighbour naming a list on some OTHER board (neither the source's
 * own nor the named destination's) is a 404 at the route, because a Zod
 * schema cannot express "on the same board as the destination" any more
 * than `cardMoveBodySchema`'s docstring says it can express "on the same
 * board as the card".
 *
 * ⚠ **Both-null is LEGAL here, unlike `listPositionPatchSchema`'s `.refine`
 * — do not carry that refusal over.** There the pair is illegal because a
 * reorder patch has no destination of its own to fall back on; here the
 * destination is the whole point of the route; and either branch above gives
 * the pair a complete, ordinary meaning. Refusing it would make
 * `FR-TMPL-014`'s own "unless a position is chosen" default inexpressible.
 */
export const listCopyBodySchema = z.strictObject({
  boardId: ulidSchema
    .optional()
    .describe(
      'The destination board. Omit to copy onto the SOURCE list’s own ' +
        'board — FR-TMPL-014: "creates a new list on the same board, or ' +
        'on a chosen board." Naming a different board makes this a ' +
        'cross-board copy and triggers the label/member reconciliation ' +
        'FR-TMPL-019/FR-TMPL-020 already settled for a move, reported in ' +
        'this response’s reconciliation.',
    ),
  previousListId: ulidSchema
    .nullable()
    .default(null)
    .describe(
      'The list immediately to the LEFT of the gap the copy goes into, ' +
        'on the DESTINATION board. Omitting both neighbours means ' +
        '"immediately after the source list" when boardId is omitted, or ' +
        '"the far right of the destination board’s strip" when boardId ' +
        'names another board — see the schema’s own docstring for the ' +
        'full argument. UNLIKE listPositionPatchSchema, both null is ' +
        'legal here.',
    ),
  nextListId: ulidSchema
    .nullable()
    .default(null)
    .describe(
      'The list immediately to the RIGHT of the gap the copy goes into, ' +
        'on the DESTINATION board. See previousListId.',
    ),
  title: listTitleSchema
    .optional()
    .describe(
      'The copy’s title. Omit to inherit the source’s title, suffixed ' +
        'with " (2)", " (3)", … on a collision with a live sibling list ' +
        'on the DESTINATION board (FR-TMPL-016) — the suffixing itself is ' +
        'apps/server/src/copy/duplicate-name.ts’s nextCopyName, not a ' +
        'transform this schema performs. Provide a value to use it ' +
        'exactly, with no suffix ever, even if it collides.',
    ),
});

export type ListCopyBody = z.infer<typeof listCopyBodySchema>;

/**
 * `POST /boards/:id/copy` — `FR-TMPL-015`.
 *
 * The board is in the path; the destination **workspace** and the copy's
 * **visibility** are required body fields, `keepCards` decides whether the
 * source's cards travel at all, and `title` is optional on `FR-TMPL-016`'s
 * usual footing. No per-element toggle of any kind — see the module
 * header's tier table: a board copy's cards, when kept, carry titles and
 * descriptions and nothing else, unconditionally.
 *
 * ⚠ **`visibility` is a THREE-member enum, narrower than `boardVisibility
 * Schema`'s four, and inline rather than imported — `boardRolePatchSchema`'s
 * own "two out, three in" precedent (`./board`).** `enterprise_visible` has
 * no read arm anywhere in the product yet (`BE-67.1` owns it); accepting it
 * here would store a value nothing can read and hand the caller a success
 * for a restriction that silently does not apply, on the identical failure
 * `BOARD_VISIBILITIES`'s own docstring names. A board copy is a fresh board,
 * so — unlike `createBoardBodySchema`, which accepts no visibility at all
 * and always takes the column default — this route DOES let the actor
 * choose one, on `FR-TMPL-002`/`FR-TMPL-015`'s own behaviour text ("lets the
 * member pick the destination workspace and visibility"); it is simply
 * refused the one member nothing can act on yet.
 *
 * ⚠ **There is deliberately no `confirmWorldReadable` companion field, and
 * that is a flagged gap rather than a considered omission — see this
 * block's final report.** `PATCH /boards/:id/visibility` (`./board`,
 * `boardVisibilityPatchSchema`) requires that literal confirmation before a
 * board becomes world-readable, on `FR-PERM-010`'s own words. Nothing in
 * `FR-TMPL-002`/`FR-TMPL-015` states whether choosing `'public'` at COPY
 * time needs the identical acknowledgement, and the block brief this file
 * implements does not settle it either. Adding a fourth key that exists on
 * only one arm of a discriminated union here would change the shape every
 * other agent building `POST /boards/:id/copy` and `FE-35.2` is already
 * writing against, mid-block — the deviation this package's own rule
 * exists to prevent — so this body stays the flat shape the brief
 * enumerated (`workspaceId`, `visibility`, `keepCards`, optional `title`)
 * and the gap is recorded rather than silently closed here.
 */
export const boardCopyBodySchema = z.strictObject({
  workspaceId: ulidSchema.describe(
    'The destination workspace. Required — FR-TMPL-002/FR-TMPL-015: the ' +
      'copy dialog "requires a title and lets the member pick the ' +
      'destination workspace and visibility". `boards.workspace_id` is ' +
      'NOT NULL, so there is no board-less branch, on createBoardBody ' +
      'Schema’s footing.',
  ),
  visibility: z
    .enum(['private', 'workspace_visible', 'public'])
    .describe(
      'The copy’s visibility, chosen at creation rather than inherited ' +
        'from the source silently (the module brief: "visibility comes ' +
        'from the request, not inherited"). `enterprise_visible` is ' +
        'refused — it has no read arm anywhere in the product yet ' +
        '(BE-67.1) — on boardRolePatchSchema’s "two out, three in" ' +
        'precedent for the same class of column.',
    ),
  keepCards: z
    .boolean()
    .default(true)
    .describe(
      'Whether the source’s cards travel at all, as title + description ' +
        'only — never their checklists, labels, members, attachments or ' +
        'dates (FR-TMPL-015: "card content limited to titles and ' +
        'descriptions"). Clearing this keeps the copy’s lists and label ' +
        'set but no cards (FR-TMPL-015 AC4, FR-TMPL-002 AC2). Defaults to ' +
        'true — FR-TMPL-002/015’s own acceptance criteria are phrased as ' +
        '"given I CLEAR the keep-cards choice", which presumes it starts ' +
        'checked.',
    ),
  title: boardTitleSchema
    .optional()
    .describe(
      'The copy’s title. Omit to inherit the source’s title, suffixed ' +
        'with " (2)", " (3)", … on a collision with a live sibling board ' +
        'in the DESTINATION workspace (FR-TMPL-016) — computed by ' +
        'apps/server/src/copy/duplicate-name.ts’s nextCopyName, not by ' +
        'this schema. Provide a value to use it exactly, with no suffix ' +
        'ever, even if it collides.',
    ),
});

export type BoardCopyBody = z.infer<typeof boardCopyBodySchema>;

// ---------------------------------------------------------------------------
// Views — what the three routes RETURN.
//
// `z.object`, which STRIPS an unknown key rather than refusing it — the
// exact inverse of the inputs above, and `cross-board-move.ts`'s own trap:
// `safeParse` on an extra field SUCCEEDS, so an assertion that a field is
// ABSENT from one of these must go through `Object.keys(schema.shape)`.
// ---------------------------------------------------------------------------

/**
 * What `POST /cards/:id/copy` answers with — `FR-TMPL-013`.
 *
 * Field-for-field identical to `cardMoveToBoardResultSchema`
 * (`./cross-board-move`) and declared separately anyway, on that file's own
 * recorded rule: "two routes with the same shape today is not two routes
 * with the same contract." `reconciliation` is `reconciliationSchema`
 * imported whole — see the module header for why this pairing genuinely
 * does share the underlying contract rather than merely coinciding with it.
 *
 * ⚠ **`card` is the whole card back (`cardDetailSchema`), not `cardSchema`**
 * — `cardMoveToBoardResultSchema`'s own argument transfers unchanged: the
 * reconciliation is only interpretable beside the card's RESULTING labels
 * and members, which only `cardDetailSchema` carries.
 */
export const cardCopyResultSchema = z.object({
  card: cardDetailSchema,
  reconciliation: reconciliationSchema,
});

export type CardCopyResult = z.infer<typeof cardCopyResultSchema>;

/**
 * What `POST /lists/:id/copy` answers with — `FR-TMPL-014`.
 *
 * `{ list, cardsCopied, reconciliation }`, on `listMoveToBoardResultSchema`'s
 * exact shape and for the exact reason: a list can hold any number of cards,
 * so returning N card backs would make one gesture answer with an unbounded
 * response, and `FR-TMPL-014`'s own edge case — "copying a very large list
 * should report progress or completion rather than appearing to hang" — is
 * a reader for the count rather than for the array. The client refetches
 * `GET /boards/:id/contents` for the cards themselves, exactly as it does
 * after a cross-board list move.
 *
 * `cardsCopied` mirrors `cardsMoved`'s own spelling —
 * `z.number().int().nonnegative()`, never `z.int().min(0)` — the house rule
 * that file states in full: `.int()` and `.nonnegative()` discriminate on
 * different inputs (`2.5` and `-1` respectively) and neither subsumes the
 * other.
 */
export const listCopyResultSchema = z.object({
  list: listSchema,
  cardsCopied: z.number().int().nonnegative(),
  reconciliation: reconciliationSchema,
});

export type ListCopyResult = z.infer<typeof listCopyResultSchema>;

/**
 * What `POST /boards/:id/copy` answers with — `FR-TMPL-015`.
 *
 * `{ board, cardsCopied }` — no `lists` array (the client refetches
 * `GET /boards/:id/contents`, `listMoveToBoardResultSchema`'s precedent
 * against an unbounded response) and, unlike the other two results, **no
 * `reconciliation`** — see the module header for why a board copy has none
 * to report.
 *
 * `cardsCopied` is the one number this response needs and `FR-TMPL-015`
 * itself supplies the sentence that reads it: AC4, "given I clear 'keep
 * cards', … it has the same lists and label set but no cards" — a
 * `cardsCopied: 0` on that path is the response making the acceptance
 * criterion assertable without a second read. There is deliberately no
 * `listsCopied` beside it: nothing in `FR-TMPL-015`/`FR-TMPL-002` reads a
 * list count back from this response the way AC4 reads a card count, and a
 * field with no acceptance criterion behind it is exactly how `BE-02.1`
 * quietly becomes unreviewable (`./card`'s own rule, restated once more).
 */
export const boardCopyResultSchema = z.object({
  board: boardSchema,
  cardsCopied: z.number().int().nonnegative(),
});

export type BoardCopyResult = z.infer<typeof boardCopyResultSchema>;
